import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Card, CardContent, Grid,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import JournalLegsTable from '../../components/admin/ledger/JournalLegsTable';
import { fetchLedgerJournal, clearJournal } from '../../store/slices/ledgerSlice';

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN') : '-';

export default function JournalDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { journal, loading, error } = useSelector(s => s.ledger);

  useEffect(() => {
    if (id) dispatch(fetchLedgerJournal(id));
    return () => { dispatch(clearJournal()); };
  }, [id, dispatch]);

  const j = journal;

  return (
    <AdminLayout title="Journal Detail">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/ledger')} variant="outlined" size="small">
            Back to Ledger
          </Button>
          <Typography variant="h5" fontWeight="bold">Journal Detail</Typography>
        </Box>

        {loading.journal && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error.journal && (
          <Alert severity="error">{error.journal}</Alert>
        )}

        {j && (
          <Box>
            {/* Journal metadata */}
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Journal ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {j.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={j.journal_type} size="small" color="primary" />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={j.status || 'POSTED'}
                        size="small"
                        color={j.status === 'REVERSED' ? 'error' : 'success'}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Consultation ID</Typography>
                    <Typography variant="body2">{j.consultation_id ?? '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Service Type</Typography>
                    <Typography variant="body2">{j.service_type ?? '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body2">{fmtDate(j.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <Typography variant="caption" color="text.secondary">Created By</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{j.created_by}</Typography>
                  </Grid>
                  {j.description && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{j.description}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Journal legs */}
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Ledger Entries ({(j.entries || []).length} legs)
            </Typography>
            <JournalLegsTable entries={j.entries || []} />
          </Box>
        )}
      </Container>
    </AdminLayout>
  );
}
